import { createTheme, ThemeOptions } from '@mui/material/styles';

// Professional Color Palette
const colors = {
  // Primary Colors
  primary: {
    main: '#3498db',
    light: '#5dade2',
    dark: '#2980b9',
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#e74c3c',
    light: '#ec7063',
    dark: '#c0392b',
    contrastText: '#ffffff',
  },
  
  // Success Colors
  success: {
    main: '#27ae60',
    light: '#58d68d',
    dark: '#1e8449',
    contrastText: '#ffffff',
  },
  
  // Warning Colors
  warning: {
    main: '#f39c12',
    light: '#f7dc6f',
    dark: '#d68910',
    contrastText: '#ffffff',
  },
  
  // Error Colors
  error: {
    main: '#e74c3c',
    light: '#ec7063',
    dark: '#c0392b',
    contrastText: '#ffffff',
  },
  
  // Info Colors
  info: {
    main: '#3498db',
    light: '#5dade2',
    dark: '#2980b9',
    contrastText: '#ffffff',
  },
  
  // Text Colors
  text: {
    primary: '#2c3e50',
    secondary: '#7f8c8d',
    disabled: '#bdc3c7',
  },
  
  // Background Colors
  background: {
    default: '#fafbfc',
    paper: '#ffffff',
    accent: '#f8f9fa',
  },
  
  // Border Colors
  border: {
    main: '#e1e8ed',
    light: '#ecf0f1',
    dark: '#bdc3c7',
  },
  
  // Status Colors
  status: {
    present: '#27ae60',
    absent: '#e74c3c',
    pending: '#f39c12',
    approved: '#27ae60',
    rejected: '#e74c3c',
  },
  
  // Chart Colors
  chart: {
    primary: '#3498db',
    secondary: '#e74c3c',
    success: '#27ae60',
    warning: '#f39c12',
    info: '#17a2b8',
    purple: '#9b59b6',
    orange: '#e67e22',
    teal: '#1abc9c',
  }
};

// Typography Configuration
const typography = {
  fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  h1: {
    fontWeight: 700,
    fontSize: '2.5rem',
    lineHeight: 1.2,
    color: colors.text.primary,
  },
  h2: {
    fontWeight: 700,
    fontSize: '2rem',
    lineHeight: 1.3,
    color: colors.text.primary,
  },
  h3: {
    fontWeight: 700,
    fontSize: '1.75rem',
    lineHeight: 1.3,
    color: colors.text.primary,
  },
  h4: {
    fontWeight: 600,
    fontSize: '1.5rem',
    lineHeight: 1.4,
    color: colors.text.primary,
  },
  h5: {
    fontWeight: 600,
    fontSize: '1.25rem',
    lineHeight: 1.4,
    color: colors.text.primary,
  },
  h6: {
    fontWeight: 600,
    fontSize: '1.125rem',
    lineHeight: 1.4,
    color: colors.text.primary,
  },
  subtitle1: {
    fontWeight: 500,
    fontSize: '1rem',
    lineHeight: 1.5,
    color: colors.text.secondary,
  },
  subtitle2: {
    fontWeight: 500,
    fontSize: '0.875rem',
    lineHeight: 1.5,
    color: colors.text.secondary,
  },
  body1: {
    fontWeight: 400,
    fontSize: '1rem',
    lineHeight: 1.6,
    color: colors.text.primary,
  },
  body2: {
    fontWeight: 400,
    fontSize: '0.875rem',
    lineHeight: 1.6,
    color: colors.text.secondary,
  },
  button: {
    fontWeight: 600,
    fontSize: '0.875rem',
    lineHeight: 1.5,
    textTransform: 'none',
  },
  caption: {
    fontWeight: 400,
    fontSize: '0.75rem',
    lineHeight: 1.5,
    color: colors.text.secondary,
  },
  overline: {
    fontWeight: 600,
    fontSize: '0.75rem',
    lineHeight: 1.5,
    textTransform: 'uppercase',
    color: colors.text.secondary,
  },
};

// Component Style Overrides
const components = {
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        textTransform: 'none',
        fontWeight: 600,
        boxShadow: 'none',
        '&:hover': {
          boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
        },
      },
      contained: {
        '&:hover': {
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        },
      },
      outlined: {
        borderWidth: 1.5,
        '&:hover': {
          borderWidth: 1.5,
        },
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: 12,
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        border: `1px solid ${colors.border.main}`,
        '&:hover': {
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
        },
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        borderRadius: 12,
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        border: `1px solid ${colors.border.main}`,
      },
      elevation1: {
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      },
      elevation2: {
        boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
      },
      elevation3: {
        boxShadow: '0 8px 32px rgba(0,0,0,0.16)',
      },
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          borderRadius: 8,
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.primary.main,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.primary.main,
            borderWidth: 2,
          },
        },
      },
    },
  },
  MuiSelect: {
    styleOverrides: {
      root: {
        borderRadius: 8,
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: 16,
        fontWeight: 500,
      },
    },
  },
  MuiTabs: {
    styleOverrides: {
      root: {
        '& .MuiTab-root': {
          textTransform: 'none',
          fontWeight: 500,
          minHeight: 48,
          '&.Mui-selected': {
            fontWeight: 600,
          },
        },
        '& .MuiTabs-indicator': {
          height: 3,
          borderRadius: '3px 3px 0 0',
        },
      },
    },
  },
  MuiDataGrid: {
    styleOverrides: {
      root: {
        border: 'none',
        '& .MuiDataGrid-row': {
          borderRadius: 8,
          margin: '2px 0',
          '&:hover': {
            backgroundColor: colors.background.accent,
            transform: 'translateY(-1px)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            transition: 'all 0.2s ease-in-out',
          },
        },
        '& .MuiDataGrid-columnHeaders': {
          backgroundColor: colors.primary.main,
          color: colors.primary.contrastText,
          borderRadius: 8,
          display: 'flex !important',
          visibility: 'visible !important',
          opacity: '1 !important',
          minHeight: '56px !important',
          '& .MuiDataGrid-columnHeader': {
            border: 'none',
            display: 'flex !important',
            visibility: 'visible !important',
            opacity: '1 !important',
            '&:hover': {
              backgroundColor: colors.primary.dark,
            },
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            visibility: 'visible !important',
            opacity: '1 !important',
          },
          '& .MuiDataGrid-columnHeaderTitleContainer': {
            visibility: 'visible !important',
            opacity: '1 !important',
          },
        },
        '& .MuiDataGrid-cell': {
          border: 'none',
          padding: '12px 16px',
        },
        '& .MuiDataGrid-footerContainer': {
          backgroundColor: colors.background.accent,
          borderRadius: 8,
        },
      },
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: {
        borderRadius: 16,
        boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        border: `1px solid ${colors.border.main}`,
      },
    },
  },
  MuiDialogTitle: {
    styleOverrides: {
      root: {
        backgroundColor: colors.primary.main,
        color: colors.primary.contrastText,
        borderRadius: '16px 16px 0 0',
        padding: '20px 24px',
      },
    },
  },
  MuiAccordion: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        border: `1px solid ${colors.border.main}`,
        '&:before': {
          display: 'none',
        },
      },
    },
  },
  MuiTable: {
    styleOverrides: {
      root: {
        '& .MuiTableCell-root': {
          borderColor: colors.border.main,
        },
      },
    },
  },
};

// Create the theme
const theme = createTheme({
  palette: {
    primary: colors.primary,
    secondary: colors.secondary,
    success: colors.success,
    warning: colors.warning,
    error: colors.error,
    info: colors.info,
    text: colors.text,
    background: colors.background,
    divider: colors.border.main,
  },
  typography,
  components,
  shape: {
    borderRadius: 8,
  },
  shadows: [
    'none',
    '0 2px 8px rgba(0,0,0,0.08)',
    '0 4px 16px rgba(0,0,0,0.12)',
    '0 8px 32px rgba(0,0,0,0.16)',
    '0 16px 64px rgba(0,0,0,0.20)',
    ...Array(20).fill('none'), // Fill remaining shadows
  ],
} as ThemeOptions);

// Export the theme and colors for use in components
export { theme, colors };
export default theme; 